import { useState, useEffect, useMemo } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import * as actions from '../../../redux/actions/growthEngine';
import { toast, ToastContainer } from "react-toastify";
import { ModalShell } from "./ModalShell";
import {
    getAccountTodayString,
    calendarDateFromString,
    isAccountCalendarToday,
    toScheduleDateString,
    getCampaignMinBudget,
    formatCampaignMoney,
} from '../../../helper/utility';


export const DuplicateCampaignModal = ({
    show,
    close,
    initialBudget,
    currency,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const [isVisible, setIsVisible] = useState(show);
    const { duplicatedCamapignDetails } = useSelector(state => state.growthEngine);
    const [showLoading, setShowLoading] = useState(false);
    const accountTimezone = duplicatedCamapignDetails?.timezone || "";
    const accountTodayStr = useMemo(
        () => getAccountTodayString(accountTimezone),
        [accountTimezone]
    );

    useEffect(() => {
        if (show) {
            setIsVisible(true);
        } else {
            const timeout = setTimeout(() => setIsVisible(false), 300);
            return () => clearTimeout(timeout);
        }
    }, [show]);

    const [currentMonth, setCurrentMonth] = useState(() => {
        return calendarDateFromString(getAccountTodayString("")) || new Date();
    });
    const { startDate, endDate, budgetType, budget, minDailyBudget } = duplicatedCamapignDetails;
    const currencyLabel = duplicatedCamapignDetails.currency || currency || "USD";
    const minBudget = useMemo(() => Math.ceil(getCampaignMinBudget({
        budgetType,
        minDailyBudget,
        startDate,
        startTime: null,
        endDate,
        endTime: null,
    }) || 0), [budgetType, minDailyBudget, startDate, endDate]);

    useEffect(() => {
        const todayDate = calendarDateFromString(accountTodayStr);
        if (todayDate) setCurrentMonth(todayDate);
    }, [accountTimezone]);

    useEffect(() => {
        if (!show) return undefined;
        const adAccountId = duplicatedCamapignDetails?.adAccountId;
        if (!adAccountId) return undefined;
        let cancelled = false;
        (async () => {
            const limits = await dispatch(actions.loadAdAccountMinBudget(adAccountId, toast));
            if (!cancelled && limits?.minDailyBudget) {
                dispatch(actions.updateDuplicateCampaign({ minDailyBudget: limits.minDailyBudget }));
            }
        })();
        return () => { cancelled = true; };
    }, [show, duplicatedCamapignDetails?.adAccountId, dispatch]);

    // Only bump when the required min changes (dates / budget type), not while typing
    useEffect(() => {
        if (!minBudget) return;
        const current = Number(budget);
        if (!Number.isNaN(current) && current > 0 && current < minBudget) {
            dispatch(actions.updateDuplicateCampaign({ budget: minBudget }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minBudget, dispatch]);

    const startOfCurrentMonth = startOfMonth(currentMonth);
    const endOfCurrentMonth = endOfMonth(currentMonth);

    const daysInMonth = eachDayOfInterval({
        start: startOfCurrentMonth,
        end: endOfCurrentMonth,
    });

    const goToNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
    const goToPreviousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

    const handleDateClick = async (date) => {
        const selectedStr = toScheduleDateString(date);

        // Duplicate flow: require a day after account-today
        if (!selectedStr || selectedStr <= accountTodayStr) {
            toast.warning("Please select a future date in your ad account timezone", { position: "top-center" });
            return;
        }

        const selectedDate = calendarDateFromString(selectedStr) || date;

        const setDateWithTime = (baseDate, hours, minutes) => {
            const newDate = new Date(baseDate);
            newDate.setHours(hours, minutes, 0, 0);
            return newDate;
        };

        // if no start → set start
        if (!startDate || (startDate && endDate)) {
            const start = setDateWithTime(selectedDate, 14, 30); // 2:30 PM
            await dispatch(actions.updateDuplicateCampaign({ startDate: start, endDate: null }));
            return;
        }

        // if start exists → set end
        if (startDate && !endDate) {
            if (selectedStr < toScheduleDateString(startDate)) {
                toast.warning("End date cannot be before start date", {
                    position: "top-center",
                });
                return;
            }
            const end = setDateWithTime(selectedDate, 23, 45); // 11:45 PM
            await dispatch(actions.updateDuplicateCampaign({ endDate: end }));
        }
    };

    const isWithinRange = (date) => {
        if (startDate && endDate) {
            const day = toScheduleDateString(date);
            const startStr = toScheduleDateString(startDate);
            const endStr = toScheduleDateString(endDate);
            return day > startStr && day < endStr;
        }
        return false;
    };

    const isSelectedStart = (date) => toScheduleDateString(date) === toScheduleDateString(startDate);
    const isSelectedEnd = (date) => toScheduleDateString(date) === toScheduleDateString(endDate);

    const handleSave = async () => {
        if (!budget || budget <= 0) {
            toast.error("Budget must be greater than 0", { position: "top-center", theme: "colored" });
            return;
        }
        if (minBudget && Number(budget) < minBudget) {
            toast.error(
                budgetType === "lifetime"
                    ? `Minimum lifetime budget for ${currencyLabel} is ${formatCampaignMoney(minBudget, currencyLabel)}. Please increase your budget or decrease the length of your campaign.`
                    : `Minimum daily budget for ${currencyLabel} is ${formatCampaignMoney(minBudget, currencyLabel)}`,
                { position: "top-center", theme: "colored" }
            );
            dispatch(actions.updateDuplicateCampaign({ budget: minBudget }));
            return;
        }
        if (budgetType === 'lifetime') {
            if (!startDate || !endDate) {
                toast.error("Please select both start and end dates", { position: "top-center", theme: "colored" });
                return;
            }
        }else if(budgetType === 'daily'){
            if (!startDate) {
                toast.error("Please select start date", { position: "top-center", theme: "colored" });
                return;
            }
        }
        if (endDate && endDate <= startDate) {
            toast.error("End date must be after start date", { position: "top-center", theme: "colored" });
            return;
        }
        navigate(`/admin/growth-engine/duplicate-campaign`)
        close()
    };

    const budgetTypeChange = async (value) => {
        await dispatch(actions.updateDuplicateCampaign({ budgetType: value }));
    }

    const footer = (
        <div className="flex items-center justify-end gap-3">
            <button
                onClick={close}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
                Cancel
            </button>
            <button
                onClick={handleSave}
                className="flex items-center gap-2 rounded-full bg-theme_clr2 px-6 py-2 text-sm font-medium text-white hover:bg-theme_clr4"
                disabled={showLoading}
            >
                {showLoading && <i className="bi bi-arrow-repeat animate-spin"></i>}
                {showLoading ? "Updating..." : "Set"}
            </button>
        </div>
    );

    return (
        <>
            <ToastContainer />
            <ModalShell
                isOpen={show}
                isVisible={isVisible}
                onClose={close}
                title="Duplicate Campaign"
                subtitle="Update budget and schedule before continuing."
                footer={footer}
            >
                {/* Budget Type */}
                <div className="mb-5">
                    <label className="form-label font-medium">Edit Budget Type</label>
                    <div className="mt-2 flex flex-wrap gap-6">
                        <label className="flex cursor-pointer items-center gap-2">
                            <input
                                type="radio"
                                name="budgetType"
                                value="daily"
                                checked={duplicatedCamapignDetails?.budgetType === "daily"}
                                onChange={() => budgetTypeChange("daily")}
                            />
                            <span>Daily</span>
                        </label>
                        <label className="flex cursor-pointer items-center gap-2">
                            <input
                                type="radio"
                                name="budgetType"
                                value="lifetime"
                                checked={duplicatedCamapignDetails?.budgetType === "lifetime"}
                                onChange={() => budgetTypeChange("lifetime")}
                            />
                            <span>Lifetime</span>
                        </label>
                    </div>
                </div>

                {/* Budget Input */}
                <div className="mb-5">
                    <label className="form-label font-medium">Budget</label>
                    <div className="relative mt-2">
                        <input
                            type="number"
                            value={duplicatedCamapignDetails.budget ?? ""}
                            onChange={(e) => {
                                const val = e.target.value;
                                // Allow empty or partial numbers while typing
                                if (val === "" || !isNaN(Number(val))) {
                                    dispatch(actions.updateDuplicateCampaign({ budget: val }));
                                }
                            }}
                            onBlur={() => {
                                let val = Number(duplicatedCamapignDetails.budget);
                                if (isNaN(val) || duplicatedCamapignDetails.budget === "") {
                                    dispatch(actions.updateDuplicateCampaign({ budget: minBudget || "" }));
                                    return;
                                }

                                if (minBudget && val < minBudget) {
                                    dispatch(actions.updateDuplicateCampaign({ budget: minBudget }));
                                } else {
                                    dispatch(actions.updateDuplicateCampaign({ budget: val }));
                                }
                            }}
                            placeholder="Enter budget"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-16 focus:outline-none 
                                        [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="absolute inset-y-0 right-3 flex items-center text-gray-500">
                            {currency}
                        </span>
                    </div>
                    {minDailyBudget ? (
                        <div className="mt-2 text-[11px] text-slate-500">
                            {budgetType === "lifetime"
                                ? `Minimum lifetime budget for ${currencyLabel} is ${formatCampaignMoney(minBudget, currencyLabel)} (Meta daily min × campaign days).`
                                : `Minimum daily budget for ${currencyLabel} is ${formatCampaignMoney(minBudget || minDailyBudget, currencyLabel)}.`}
                        </div>
                    ) : null}
                </div>

                {/* Start & End Dates */}
                <div className="mb-2">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <label className="form-label mb-0 font-medium">Select Time Range</label>
                        {duplicatedCamapignDetails?.timezone ? (
                            <span className="text-[11px] text-slate-500">
                                Schedule uses Meta ad account timezone: {duplicatedCamapignDetails.timezone}
                            </span>
                        ) : null}
                    </div>
                    <div className="mt-2 rounded-2xl border-2 border-theme_clr1 bg-white p-3 sm:p-4">
                        <div className="calendar-wrapper mx-auto w-full max-w-[312px] text-center">
                            {(startDate && endDate) && (
                                <div className="mb-4 text-[14.25px] font-medium text-theme_clr1">
                                    {format(startDate, "MMMM dd, yyyy")} - {format(endDate, "MMMM dd, yyyy")}
                                </div>
                            )}

                            {/* Month Navigation */}
                            <div className="mb-2 flex items-center justify-between text-[12.8px] font-roboto font-medium">
                                <button className="h-[30px] w-[40px]" onClick={goToPreviousMonth}>
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                                <span>{format(currentMonth, "MMMM yyyy")}</span>
                                <button className="h-[30px] w-[40px]" onClick={goToNextMonth}>
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            </div>

                            {/* Days of the Week */}
                            <div className="grid grid-cols-7 gap-[1px] text-center">
                                {["S", "M", "T", "W", "T", "F", "S"].map((d, index) => (
                                    <div key={index} className="flex h-[35px] w-[35px] items-center justify-center text-xs font-roboto md:h-[40px] md:w-[40px]">{d}</div>
                                ))}

                                {/* Empty days before first day of month */}
                                {Array.from({ length: startOfCurrentMonth.getDay() }).map((_, index) => (
                                    <div key={index} className="p-2"></div>
                                ))}

                                {/* Days of month */}
                                {daysInMonth.map((date) => {
                                    const isStart = isSelectedStart(date);
                                    const isEnd = isSelectedEnd(date);
                                    const isPastOrToday = toScheduleDateString(date) <= accountTodayStr;

                                    return (
                                        <div
                                            key={date}
                                            onClick={() => handleDateClick(date)}
                                            className={`relative flex h-[35px] w-[35px] cursor-pointer items-center justify-center rounded-full p-1 text-sm font-inter font-medium text-theme_clr1 md:h-[40px] md:w-[40px]
                                ${isStart ? "bg-gradient-to-r from-white from-[-15%] to-theme_clr1" : ""}
                                ${isEnd ? "bg-gradient-to-r from-white from-[-15%] to-theme_clr2" : ""}
                                ${isWithinRange(date) ? "bg-blue-200" : ""}
                                ${isAccountCalendarToday(date, accountTimezone) ? "bg-yellow-300" : ""}
                                ${isPastOrToday ? "cursor-not-allowed opacity-40" : "hover:bg-gray-200"}`}
                                        >
                                            {format(date, "d")}

                                            {/* Start / End Remarks */}
                                            {isStart && (
                                                <span className="absolute -top-4 text-xs font-bold text-theme_clr1">Start</span>
                                            )}
                                            {isEnd && (
                                                <span className="absolute -top-4 text-xs font-bold text-theme_clr2">End</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </ModalShell>
        </>
    );
};
